import React, { useEffect, useState, useCallback } from "react";
import { Ticket, Trophy, Save, Trash2, Dices } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { logError } from "../lib/logger";

const inputCls = "w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-white/10 text-white text-sm focus:border-amber-500 focus:outline-none transition-colors";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};
const winnerName = (d) =>
  [d.winner_first_name, d.winner_last_name].filter(Boolean).join(" ") || d.winner_name || "Utente";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

export function LotteryAdmin() {
  const [data, setData] = useState(null);
  const [when, setWhen] = useState("");
  const [prize, setPrize] = useState("");
  const [saving, setSaving] = useState(false);
  const [drawing, setDrawing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lottery");
      setData(data);
      setWhen(toLocalInput(data.next_draw_at));
      setPrize(data.prize || "");
    } catch (e) { logError("lottery admin load", e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put("/admin/lottery", {
        next_draw_at: when ? new Date(when).toISOString() : "",
        prize,
      });
      toast.success("Impostazioni lotteria salvate");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const drawNow = async () => {
    if (!window.confirm("Eseguire subito il sorteggio tra gli abbonati VIP attivi?")) return;
    setDrawing(true);
    try {
      const { data } = await api.post("/admin/lottery/draw");
      toast.success(`Vincitore: #${data.drawn_number} · ${winnerName(data)}`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDrawing(false); }
  };

  const removeDraw = async (id) => {
    if (!window.confirm("Rimuovere questa estrazione dallo storico?")) return;
    try {
      await api.delete(`/admin/lottery/draws/${id}`);
      toast.success("Estrazione rimossa");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const draws = data?.draws || [];

  return (
    <div data-testid="admin-lottery" className="mb-8 bg-[#141414] border border-amber-500/25 rounded-md p-5">
      <div className="flex items-center gap-2 mb-1">
        <Ticket size={16} className="text-amber-400" />
        <h2 className="text-base font-semibold text-white">Lotteria · Estrazione premi</h2>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Imposta data/ora della prossima estrazione (countdown pubblico) e il premio. Al raggiungimento dell'orario il sistema
        sorteggia automaticamente un numero tra gli <span className="text-zinc-300">abbonati VIP attivi</span> · {data?.participants_count ?? 0} partecipanti.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Data e ora estrazione</label>
          <input data-testid="lottery-when" type="datetime-local" value={when}
            onChange={(e) => setWhen(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Premio in palio</label>
          <input data-testid="lottery-prize" type="text" value={prize} placeholder="Es. 1 mese VIP gratis"
            onChange={(e) => setPrize(e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <button data-testid="lottery-save-btn" onClick={saveConfig} disabled={saving}
          className="flex items-center gap-2 px-5 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-500 transition-colors disabled:opacity-50">
          <Save size={15} /> {saving ? "Salvo…" : "Salva impostazioni"}
        </button>
        <button data-testid="lottery-draw-btn" onClick={drawNow} disabled={drawing}
          className="flex items-center gap-2 px-5 py-2 rounded-md bg-white/5 border border-emerald-500/30 text-emerald-400 text-sm font-medium hover:bg-emerald-500/15 transition-colors disabled:opacity-50">
          <Dices size={15} /> {drawing ? "Sorteggio…" : "Sorteggia ora"}
        </button>
        {when && (
          <button data-testid="lottery-clear-btn"
            onClick={() => { setWhen(""); }}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-white/5 border border-white/10 text-zinc-400 text-sm hover:text-white transition-colors">
            Azzera data
          </button>
        )}
      </div>

      {draws.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center gap-2 text-sm font-medium text-white mb-3">
            <Trophy size={15} className="text-amber-400" /> Storico estrazioni ({draws.length})
          </div>
          <div className="space-y-2">
            {draws.map((d) => (
              <div key={d.id} data-testid={`admin-draw-${d.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-white/10 bg-black/30 px-4 py-2.5">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-mono font-bold text-amber-400">#{d.drawn_number}</span>
                  <span className="text-white">{winnerName(d)}</span>
                  <span className="text-[11px] text-zinc-600">{fmtDate(d.drawn_at)}</span>
                </div>
                <button data-testid={`admin-draw-del-${d.id}`} onClick={() => removeDraw(d.id)}
                  className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors" title="Rimuovi">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
